import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Loader2, LogOut, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

/**
 * Full-screen lock rendered whenever the signed-in user has a scheduled
 * deletion. Overrides all app routing (PaidRoute, AdminRoute, etc.).
 *
 * From here the user can either restore the account or sign out. Reading
 * the screen does NOT extend the window — `deletion_effective_at` is set
 * once at schedule time.
 */
const AccountScheduledForDeletion = () => {
  const navigate = useNavigate();
  const { deletionEffectiveAt, signOut, refreshProfile } = useAuth();
  const [restoring, setRestoring] = useState(false);
  const [now, setNow] = useState(() => new Date());

  // Update countdown when the tab regains focus.
  useEffect(() => {
    const tick = () => setNow(new Date());
    document.addEventListener("visibilitychange", tick);
    window.addEventListener("focus", tick);
    return () => {
      document.removeEventListener("visibilitychange", tick);
      window.removeEventListener("focus", tick);
    };
  }, []);

  const { fmtDate, daysLeft } = useMemo(() => {
    if (!deletionEffectiveAt) return { fmtDate: "—", daysLeft: 0 };
    const end = new Date(deletionEffectiveAt);
    const ms = end.getTime() - now.getTime();
    const days = Math.max(0, Math.ceil(ms / (24 * 60 * 60 * 1000)));
    return {
      fmtDate: end.toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      }),
      daysLeft: days,
    };
  }, [deletionEffectiveAt, now]);

  const handleRestore = async () => {
    setRestoring(true);
    try {
      const { data, error } = await supabase.functions.invoke("restore-account", {
        body: {},
      });
      if (error || !data?.restored) {
        toast({
          title: "Couldn't restore account",
          description: error?.message ?? "Please try again.",
          variant: "destructive",
        });
        return;
      }
      await refreshProfile();
      toast({
        title: "Account restored",
        description:
          data.destination === "pricing"
            ? "Welcome back. Pick a plan to continue — your progress is intact."
            : "Welcome back. Your subscription continues uninterrupted.",
      });
      navigate(data.destination === "pricing" ? "/pricing" : "/dashboard", {
        replace: true,
      });
    } finally {
      setRestoring(false);
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate("/", { replace: true });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-secondary px-4 py-10">
      <Card className="max-w-lg w-full border-destructive/30">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" />
            Account scheduled for deletion
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-sm">
          <p className="text-foreground">
            Your account will be permanently deleted on{" "}
            <strong>{fmtDate}</strong>
            {daysLeft > 0 ? ` - ${daysLeft} day${daysLeft === 1 ? "" : "s"} remaining.` : "."}
          </p>
          <p className="text-muted-foreground">
            Until then, the app is locked. Restore your account to continue using it.
            Your progress, practice history, and exam results are preserved.
          </p>
          <p className="text-xs text-muted-foreground">
            Note: any active subscription ends at the close of its current billing
            period. No refund is issued for unused time.
          </p>
          <div className="flex flex-col sm:flex-row gap-2 pt-2">
            <Button onClick={handleRestore} disabled={restoring} className="sm:flex-1">
              {restoring ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Restoring…
                </>
              ) : (
                <>
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Restore account
                </>
              )}
            </Button>
            <Button variant="outline" onClick={handleSignOut} disabled={restoring}>
              <LogOut className="h-4 w-4 mr-2" />
              Sign out
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AccountScheduledForDeletion;